import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { Grid, Card, CardMedia, CardContent, Typography, LinearProgress, createStyles, makeStyles, Theme, Box } from '@material-ui/core';
import { setExceptionHandler, setSamlBody, setSamlMethod, setRedirectAfterSignin, resetLaunch } from '@app/store/actions';
import { basicService, launchService } from '@app/services';
import { createTranslation, isFirefox } from '@app/helpers';
import { getOriginUrl } from './utils';

const FROM_WEB_WORKFLOW_WEB_PAGE = 'fromWebWorkflowWebPage';
const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        media: {
            width: '15%'
        },
        appName: {
            marginBottom: '12px'
        },
        spaceAbove: {
            marginTop: '18px'
        }
    })
);

const LaunchPage = () => {
    const classes = useStyles();
    const samlBody = useSelector((state: any) => state.launch.samlBody);
    const samlMethod = useSelector((state: any) => state.launch.samlMethod);

    const isChrome: boolean = !!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime);
    const isWorkflowLaunch: boolean = window.location.search.includes('WorkflowId');
    const isExtensionInstalled: boolean = document.getElementsByTagName('html')[0].getAttribute('data-passly-sso-extension') === 'true';

    const [appImage, setAppImage] = useState<string>('');
    const [appName, setAppName] = useState<string>('');
    const [appDomain, setAppDomain] = useState<string>('');
    const [launchPolicyMessage, setLaunchPolicyMessage] = useState<string>('');
    const [permitted, setPermitted] = useState<boolean>(true);
    const [loaded, setLoaded] = useState<boolean>(false);

    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    useEffect(() => {
        dispatch(setExceptionHandler(false));
        const queryParams: string = window.location.search;

        dispatch(setRedirectAfterSignin(window.location.pathname + queryParams));

        if (!tryProtocols(queryParams, false)) {
            launchService.getTrustDetect(queryParams, handleTrustDetectResponse);
        }
    }, []);

    const showPolicyMessage = (message: string) => {
        if (message) {
            setLaunchPolicyMessage(translation(createTranslation(message)));
        } else {
            setLaunchPolicyMessage(translation('APP_POLICY_DENY_MESSAGE'));
        }

        setPermitted(false);
    };

    const handleTrustDetectResponse = (response: any) => {
        if (response.data.signOut) {
            tryProtocols(response.data.parameters.reply, response.data.signOut);
            return;
        }

        if (response.data && response.data.redirect) {
            basicService.redirect(decodeURIComponent(response.data.redirect));
            return;
        }

        let queryObject = response.data.parameters.parameters || response.data.parameters;

        let query =
            '?' +
            Object.keys(queryObject)
                .map(function (key) {
                    if (queryObject[key]) {
                        return encodeURIComponent(key) + '=' + encodeURIComponent(queryObject[key]);
                    }
                })
                .join('&');

        tryProtocols(query, response.data.parameters.signOut);
    };

    const launchWorkflow = (response: any) => {
        let workflowApp = response.data;

        if (!workflowApp.Permitted) {
            if (workflowApp.Url !== null && workflowApp.Url !== '') {
                basicService.redirect(workflowApp.Url);
            } else if (workflowApp.Workflow) {
                showPolicyMessage(workflowApp.Workflow);
            }

            return;
        }

        workflowApp.url = window.location.href;
        workflowApp.SSOAOperation = 'LaunchWorkflow';

        if (isChrome || isFirefox()) {
            window.postMessage(
                {
                    direction: FROM_WEB_WORKFLOW_WEB_PAGE,
                    workflowApp
                },
                getOriginUrl()
            );
        }
    };

    const launchSaml = (response: any) => {
        const samlResponse: any = response.data;

        if (!samlResponse.permitted) {
            if (samlResponse.replyTo) {
                basicService.redirect(samlResponse.replyTo);
            } else if (samlResponse.relayState) {
                showPolicyMessage(samlResponse.relayState);
                setLoaded(true);
            }
            return;
        }

        dispatch(resetLaunch());

        let token = samlResponse.token,
            replyTo = samlResponse.replyTo,
            relayState = samlResponse.relayState;

        setAppName(samlResponse.displayName);
        setAppImage(samlResponse.iconUrl);
        let url = new URL(samlResponse.replyTo);
        setAppDomain(url.protocol + '//' + url.hostname);

        setLoaded(true);

        (document as any).querySelector('#samlForm').action = replyTo;
        (document as any).querySelector('#SAMLResponse').value = token;
        (document as any).querySelector('#RelayState')!.value = relayState;

        setTimeout(function () {
            (document as any).querySelector('#samlForm').submit();
        }, 1000);
    };

    const launchWsFederation = (response: any) => {
        const wsFedResponse = response.data;

        if (!wsFedResponse.permitted) {
            if (wsFedResponse.replyTo) {
                basicService.redirect(wsFedResponse.replyTo);
            } else if (wsFedResponse.relayState) {
                showPolicyMessage(wsFedResponse.relayState);
                setLoaded(true);
            }
            return;
        }

        setAppName(wsFedResponse.displayName);
        setAppImage(wsFedResponse.iconUrl);

        try {
            let url = new URL(wsFedResponse.replyTo);
            setAppDomain(url.protocol + '//' + url.hostname);
        } catch (e) {
            // TODO: Uncomment when we will set up dev variable
            // console.log(e);
        }

        setLoaded(true);
        dispatch(resetLaunch());

        let token = wsFedResponse.token,
            replyTo = wsFedResponse.replyTo,
            relayState = wsFedResponse.relayState;

        let form = (document as any).querySelector('#wsfedForm');

        form.action = replyTo;

        let params = Object.keys(wsFedResponse.parameters);

        params.forEach(function (p) {
            let hidden = document.createElement('input');

            hidden.type = 'hidden';
            hidden.name = hidden.id = p;
            hidden.value = wsFedResponse.parameters[p];

            form.appendChild(hidden);
        });

        setTimeout(function () {
            form.submit();
        }, 1000);
    };

    const trySamlForm = (model: any, isElevatedPost: boolean) => {
        let request: any = {};

        if (model !== null && model !== undefined) {
            try {
                let txt = document.createElement('textarea');
                txt.innerHTML = atob(model);

                request = JSON.parse(txt.value);
            } catch (e) {
                // TODO: Uncomment when we will set up dev variable
                // console.error(e);
                return;
            }
        } else {
            request = samlBody;
            dispatch(setSamlMethod(''));
            dispatch(setSamlBody(''));
        }

        if (!isElevatedPost) {
            dispatch(setSamlMethod('POST'));
            dispatch(setSamlBody(request));
        }

        // samlPost
        launchService.postSaml(request, launchSaml);
    };

    const tryProtocols = (queryParams: string, signOut: boolean): boolean => {
        if (signOut) {
            basicService.redirect(queryParams);
        }

        // workflow launch
        if (isWorkflowLaunch) {
            if ((isChrome || isFirefox()) && isExtensionInstalled) {
                launchService.getWorkflowData(queryParams, launchWorkflow);
            }

            setLoaded(true);
            return true;
        }

        // deeplink requires detect flow
        if (queryParams && queryParams.indexOf('deepLink') >= 0) {
            return false;
        }

        // saml post
        const isElevatedPost = samlMethod === 'POST';

        if ((window as any).viewModel || isElevatedPost) {
            // viewModel is appended to the window from the trust/launch route in HomeController
            trySamlForm((window as any).viewModel, isElevatedPost);
            return true;
        }

        // saml redirect
        if ((queryParams && queryParams.indexOf('applicationId') >= 0) || queryParams.indexOf('SAMLRequest') >= 0) {
            launchService.getSaml(queryParams, launchSaml);
            return true;
        }

        // WS federation
        if (queryParams.indexOf('wsignin1.0') >= 0) {
            launchService.getWsFederation(queryParams, launchWsFederation);
            return true;
        }

        return false;
    };

    return (
        <>
            <Grid style={{ height: '60vh' }} alignContent='center' justifyContent='center' container>
                <Grid item xs={4}>
                    {loaded && (
                        <Card>
                            <Box display={'flex'} flexDirection={'column'} alignItems={'center'} justifyContent={'center'} textAlign={'center'}>
                                {!isWorkflowLaunch && permitted && (
                                    <>
                                        <CardMedia className={`${classes.media} ${classes.spaceAbove}`} component='img' src={appImage} />
                                        <CardContent>
                                            <Typography className={classes.appName} variant='h1'>
                                                {translation('SIGNING_INTO_PREFIX')} {appName}
                                            </Typography>
                                            <code>{appDomain}</code>
                                            <LinearProgress className={classes.spaceAbove} color='primary' />
                                        </CardContent>
                                    </>
                                )}
                                {!permitted && (
                                    <>
                                        <CardContent>
                                            <Typography className={classes.appName} variant='h1'>
                                                {translation('APP_POLICY_DENY')}
                                            </Typography>
                                            <Typography variant='body1'>{launchPolicyMessage}</Typography>
                                        </CardContent>
                                    </>
                                )}
                                {isWorkflowLaunch && !(isChrome || isFirefox()) && (
                                    <CardContent>
                                        <Typography variant='h1' className={classes.appName}>
                                            {translation('SSO_ASSISTANT_NOT_SUPPORTED')}
                                        </Typography>
                                        <Typography variant='body1'>
                                            {translation('SSO_ASSISTANT_NOT_SUPPORTED_BODY_PRE')}
                                            {translation('SSO_ASSISTANT_NOT_SUPPORTED_LINK')}
                                            {translation('SSO_ASSISTANT_NOT_SUPPORTED_BODY_POST')}
                                        </Typography>
                                    </CardContent>
                                )}
                                {(isChrome || isFirefox()) && !isExtensionInstalled && isWorkflowLaunch && (
                                    <CardContent>
                                        <Typography variant='h1' className={classes.appName}>
                                            {translation('SSO_ASSISTANT_NOT_INSTALLED_CHROME')}
                                        </Typography>
                                        <Typography variant='body1'>
                                            {translation('SSO_ASSISTANT_NOT_INSTALLED_BODY_CHROME')}&nbsp;
                                            <a target='_blank' href='https://chrome.google.com/webstore/detail/majfinlljhjkdhdnakejmmjfinhfemhl'>
                                                {translation('SSO_ASSISTANT_DOWNLOAD_LINK_CHROME')}
                                            </a>
                                            .
                                        </Typography>
                                    </CardContent>
                                )}
                            </Box>
                        </Card>
                    )}
                </Grid>
            </Grid>

            <form id='samlForm' method='post'>
                <input type='hidden' name='SAMLResponse' id='SAMLResponse' />
                <input type='hidden' name='RelayState' id='RelayState' />
            </form>

            <form id='wsfedForm' method='post'></form>
        </>
    );
};

export default LaunchPage;
